import React, { useEffect, useState } from "react";
import Layout from "../layout/Layout";
import { useDispatch, useSelector } from "react-redux";
import { dashboard } from "../Redux/Action/AuthAction";
import Api from "../Config/ApiConfig";

// Enhanced icons with better styling
const icons = {
  users: (
    <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
      <svg width="24" height="24" fill="none" viewBox="0 0 24 24" className="text-white">
        <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" stroke="currentColor" strokeWidth="2"/>
      </svg>
    </div>
  ),
  active: (
    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
      <svg width="24" height="24" fill="none" viewBox="0 0 24 24" className="text-white">
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
      </svg>
    </div>
  ),
  bookings: (
    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
      <svg width="24" height="24" fill="none" viewBox="0 0 24 24" className="text-white">
        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" stroke="currentColor" strokeWidth="2"/>
      </svg>
    </div>
  ),
  revenue: (
    <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
      <svg width="24" height="24" fill="none" viewBox="0 0 24 24" className="text-white">
        <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" stroke="currentColor" strokeWidth="2"/>
      </svg>
    </div>
  ),
  transactions: (
    <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
      <svg width="24" height="24" fill="none" viewBox="0 0 24 24" className="text-white">
        <path d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="2"/>
      </svg>
    </div>
  ),
  inactive: (
    <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
      <svg width="24" height="24" fill="none" viewBox="0 0 24 24" className="text-white">
        <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
      </svg>
    </div>
  ),
  reload: (
    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" className="text-white">
      <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" stroke="currentColor" strokeWidth="2"/>
    </svg>
  )
};

// Capitalize first letter of each word
function toTitleCase(str) {
  return str.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

export default function Dashboard() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloading, setReloading] = useState(false);
  const stats = useSelector((state) => state.SubAdmin.dashboard?.data);

  // Fetch dashboard stats
  useEffect(() => {
    setLoading(true);
    dispatch(dashboard())
      .unwrap()
      .catch(() => setError("Failed to load dashboard stats."))
      .finally(() => setLoading(false));
  }, [dispatch]);

  // Force reload handler
  const handleReload = async () => {
    setReloading(true);
    setError(null);
    try {
      const info = await Api.post("/dashboard-stats/reload");
      if (info.data.status_code === 200) {
        dispatch(dashboard());
      } else {
        setError(info.data.message || "Failed to reload stats.");
      }
    } catch {
      setError("Failed to reload stats.");
    } finally {
      setReloading(false);
    }
  };

  // Color configurations for status pills
  const statusColors = {
    role: {
      "care seeker": "bg-emerald-500 hover:bg-emerald-600",
      "carer": "bg-teal-500 hover:bg-teal-600",
      "admin": "bg-gray-500 hover:bg-gray-600",
      "super admin": "bg-indigo-500 hover:bg-indigo-600",
    },
    booking: {
      "booking_request_initiated": "bg-blue-500 hover:bg-blue-600",
      "payment_confirmed": "bg-green-500 hover:bg-green-600",
      "accepted_by_carer": "bg-yellow-500 hover:bg-yellow-600",
      "carer_assigned": "bg-emerald-600 hover:bg-emerald-700",
      "rejected_by_admin": "bg-red-500 hover:bg-red-600",
      "completed": "bg-green-500 hover:bg-green-600",
      "cancellation_pending": "bg-orange-500 hover:bg-orange-600",
      "cancellation_accepted": "bg-gray-500 hover:bg-gray-600",
      "cancellation_completed": "bg-gray-500 hover:bg-gray-600",
      "amount_distribute": "bg-purple-500 hover:bg-purple-600",
    },
    transaction: {
      "success": "bg-green-500 hover:bg-green-600",
      "refund": "bg-yellow-500 hover:bg-yellow-600",
      "withdraw": "bg-blue-500 hover:bg-blue-600",
    }
  };

  const StatCard = ({ icon, value, label, delay = 0, trend, isRevenue = false }) => (
    <div 
      className={`
        dashboard-stat-card p-4 animate-fade-in-up min-h-[140px]
        ${isRevenue ? 'dashboard-revenue-card' : ''}
      `}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-emerald-50 to-transparent rounded-bl-3xl"></div>
      <div className="flex flex-col h-full">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-shrink-0">
            {icon}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className={`dashboard-stat-value font-bold text-gray-900 mb-1 ${isRevenue ? 'text-lg lg:text-xl' : 'text-xl lg:text-2xl'}`}>
            {value}
          </div>
          <div className="text-xs font-medium text-gray-600 mb-2 leading-tight">
            {label}
          </div>
          {trend && (
            <div className={`text-xs font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? '↗' : '↘'} {Math.abs(trend)}% from last month
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const StatusPill = ({ status, count, type }) => {
    const colorClass = statusColors[type]?.[status] || "bg-gray-500 hover:bg-gray-600";
    const isZero = !count;
    
    return (
      <span 
        className={`
          inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold
          transition-all duration-200 hover:scale-105 cursor-pointer
          ${isZero 
            ? 'bg-gray-100 text-gray-500 border border-gray-200' 
            : `${colorClass} text-white shadow-sm`
          }
          mr-2 mb-2
        `}
      >
        {toTitleCase(status)}: <span className="ml-1 font-bold">{count}</span>
      </span>
    );
  };

  return (
    <Layout>
      <div className="dashboard-page w-full h-full bg-gray-50">
        {/* Header Section */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600 text-sm mt-1">Monitor your platform's key metrics and performance</p>
            </div>
            <button 
              onClick={handleReload}
              disabled={reloading}
              className={`
                flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm
                transition-all duration-200 hover:scale-105
                ${reloading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-emerald-600 hover:bg-emerald-700'
                }
                text-white shadow-sm
              `}
            >
              <div className={reloading ? 'animate-spin' : ''}>
                {icons.reload}
              </div>
              <span>{reloading ? 'Reloading...' : 'Refresh'}</span>
            </button>
          </div>
        </div>

        <div className="p-6 w-full">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
              <div className="text-red-600 font-semibold mb-1">Error Loading Dashboard</div>
              <div className="text-red-500 text-sm">{error}</div>
            </div>
          ) : !stats ? (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
              <div className="text-gray-600">No data available.</div>
            </div>
          ) : (
            <div className="w-full space-y-6">
              {/* Stats Cards Grid */}
              <div className="dashboard-grid w-full">
                <StatCard
                  icon={icons.users}
                  value={stats.userStats?.totalUsers?.toLocaleString() ?? "-"}
                  label="Total Users"
                  delay={0}
                  trend={12}
                />
                <StatCard
                  icon={icons.active}
                  value={stats.userStats?.activeUsers?.toLocaleString() ?? "-"}
                  label="Active Users"
                  delay={100}
                  trend={8}
                />
                <StatCard
                  icon={icons.inactive}
                  value={stats.userStats?.inactiveUsers?.toLocaleString() ?? "-"}
                  label="Inactive Users"
                  delay={200}
                  trend={-3}
                />
                <StatCard
                  icon={icons.bookings}
                  value={stats.bookingStats?.totalBookings?.toLocaleString() ?? "-"}
                  label="Total Bookings"
                  delay={300}
                  trend={15}
                />
                 <StatCard
                  icon={icons.transactions}
                  value={stats.transactionStats?.totalTransactions?.toLocaleString() ?? "-"}
                  label="Transactions"
                  delay={500}
                  trend={18}
                />
                <StatCard
                  icon={icons.revenue}
                  value={stats.transactionStats?.totalRevenue?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }) ?? "-"}
                  label="Total Revenue"
                  delay={400}
                  trend={22}
                  isRevenue={true}
                />
               
              </div>

              {/* Detailed Stats Sections */}
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 w-full">
                {/* Users by Role */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 animate-fade-in-up" style={{ animationDelay: '600ms' }}>
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center mr-3">
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" className="text-emerald-600">
                        <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Users by Role</h3>
                  </div>
                  <div className="flex flex-wrap -m-1">
                    {stats.userStats?.usersByRole &&
                      Object.entries(stats.userStats.usersByRole).map(([role, count]) => (
                        <StatusPill key={role} status={role} count={count} type="role" />
                      ))}
                  </div>
                </div>

                {/* Bookings by Status */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 animate-fade-in-up" style={{ animationDelay: '700ms' }}>
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" className="text-blue-600">
                        <path d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Bookings by Status</h3>
                  </div>
                  <div className="flex flex-wrap -m-1">
                    {stats.bookingStats?.bookingsByStatus &&
                      Object.entries(stats.bookingStats.bookingsByStatus).map(([status, count]) => (
                        <StatusPill key={status} status={status} count={count} type="booking" />
                      ))}
                  </div>
                </div>

                {/* Transactions by Status */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 animate-fade-in-up" style={{ animationDelay: '800ms' }}>
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" className="text-purple-600">
                        <path d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Transactions by Status</h3>
                  </div>
                  <div className="flex flex-wrap -m-1">
                    {stats.transactionStats?.transactionsByStatus &&
                      Object.entries(stats.transactionStats.transactionsByStatus).map(([status, count]) => (
                        <StatusPill key={status} status={status} count={count} type="transaction" />
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
